import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { PromiseState } from 'react-refetch';
import orderBy from 'lodash.orderby';
import { stringify } from 'querystring';
import interceptConnect from 'helpers/interceptConnect';
import {
  loadCustomers,
  selectAllCustomers,
  ICustomer,
} from 'redux/reducers/customers';
import { IChartData } from 'redux/reducers/charts';
import { loadCountries } from 'redux/reducers/countries';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import { IToggleOption } from 'components/data-containers/PositionsTable/TableControls/DSPToggle';
import ChartsTabs from './Tabs/ChartsTabs';
import ChartsTopicFilters from './ChartsTopicFilters/ChartsTopicFilters';
import addLabel from 'helpers/addLabel';
import formatDate from 'helpers/formatDate';
import { ChartTypes, ICountryFlagProps } from 'types/shared';
import { ICustomerWithCharts } from 'helpers/interceptors/chartsInterceptor';
import { ICountry, IChartDataWithDetails } from './types';
import {
  Layout as TableLayout,
  PaginationWrapper,
} from './ChartTable/chartTable.style';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import ChartTable from './ChartTable';
import ChartTableLoader from './ChartTable/ChartTableLoader';
import useOffset from 'hooks/useOffset';
import Pagination from 'components/data-visualization/Table/Pagination';
import Layout from 'components/styled/Layout/Layout';
import Placeholder from 'components/data-visualization/Table/Placeholder';
import Box from 'components/elements/Box';
import useExportUrl from './utils/useExportUrl';
import { addDays } from 'date-fns';
import usePrevious from '../../hooks/usePrevious';

const globalCountry = {
  displayName: 'Global',
  flagImgLarge: '',
  flagImgMedium: '',
  flagImgSmall: '',
  id: 'WW',
  lat: 0,
  lon: 0,
};

const getAvailableChartCountries = (
  chart: IChartData,
  countries: ICountryFlagProps[]
): any[] => {
  return chart.supportedCountryIds.map((countryId: string) => {
    const countryItem = countries.find(
      (countryObject: ICountryFlagProps) => countryObject.id === countryId
    );
    if (countryId === 'WW') {
      return globalCountry;
    }
    return countryItem;
  });
};

const formatCustomers = (customers: ICustomerWithCharts[]): IToggleOption[] => {
  return customers?.map(
    ({
      displayName,
      name,
      charts,
    }: {
      displayName: string;
      name: string;
      charts: IChartData[];
    }): IToggleOption => {
      return {
        label: displayName === 'Apple' ? 'iTunes' : displayName,
        value: name === 'apple' ? 'itunes' : name,
        positionsCount: charts.length,
      };
    }
  );
};

const dataSources = (): any => {
  return {
    chartsFetch: (
      customers: ICustomer[],
      chartPage: ChartTypes
    ): {
      chartsData: {
        url: string;
        interceptionData: {
          customers: ICustomer[];
          chartPage: ChartTypes;
        };
        comparison: ChartTypes;
      };
    } => {
      return {
        chartsData: {
          url: 'chartsdata',
          interceptionData: {
            customers,
            chartPage,
          },
          comparison: chartPage,
        },
      };
    },
    chartTableFetch: (
      chartId: number,
      countryId: string,
      chartPage: ChartTypes,
      limit: number,
      offset: number,
      chartFrequency: string,
      chartDate: number
    ): any => {
      const params = stringify({
        chartId,
        countryId,
        limit,
        offset,
        chartFrequency,
        chartDate,
      });
      return {
        chartTableData: {
          url: `chartstabledata?${params}`,
          interceptionData: {
            chartId,
            countryId,
            chartPage,
            limit,
            offset,
            chartFrequency,
            date: chartDate,
          },
        },
      };
    },
  };
};

const filterCharts = (
  charts: IChartDataWithDetails[],
  field: string,
  value: any
): any =>
  charts.filter(
    (chart: IChartDataWithDetails) => (chart as any)[field] === value
  );

interface IChartsData {
  reason: string;
  customersAndCharts: ICustomerWithCharts[];
}

interface IChartTableData {
  trackData: any;
  chartName: string;
  chartDate: Date;
  reason: string;
  wmgTracksPercentage: number;
  customersAndCharts: ICustomerWithCharts[];
  url: string;
  totalTracksCount: number;
  availableDates: Date[];
}

interface IChartsProps {
  setIsTopicFilterInView: Function;
  chartPage: ChartTypes;
  chartsFetch: Function;
  chartsData: PromiseState<IChartsData>;
  isLargeScreen: boolean;
  chartTableFetch: Function;
  chartTableData: PromiseState<IChartTableData>;
  setChartInfo: Function;
  defaultCountry?: string;
}

const Charts: React.FC<IChartsProps> = (props) => {
  const {
    chartPage,
    chartsFetch,
    chartsData = PromiseState.create(),
    isLargeScreen,
    chartTableFetch,
    chartTableData,
    setChartInfo,
    defaultCountry,
  } = props;
  const isLoadingCustomers = chartsData && chartsData.pending;
  const isLoadedCustomers = chartsData && chartsData.fulfilled;
  const isLoadingChartTableData = chartTableData && chartTableData.pending;
  const isLoadedChartTableData = chartTableData && chartTableData.fulfilled;
  const uniqueCustomers = useMemo(
    () =>
      (!chartsData?.pending &&
        formatCustomers(chartsData.value.customersAndCharts)) ||
      [],
    [chartsData]
  );
  const reason =
    chartsData && !chartsData?.pending && chartsData?.value.reason
      ? new Error(chartsData?.value.reason)
      : undefined;
  const customersAndCharts = useMemo(
    () =>
      (chartsData &&
        !chartsData?.pending &&
        chartsData.value.customersAndCharts) ||
      [],
    [chartsData]
  );

  const variation = 'currentPosition';
  const title = chartPage === 'dsp' ? 'DSP Charts' : 'Official Charts';
  const customers = useSelector(selectAllCustomers);
  const [selectedCustomer, setSelectedCustomer] =
    useState<IToggleOption | null>(null);
  const [availableDSPCharts, setDSPAvailableCharts] = useState<
    IChartDataWithDetails[]
  >([]);
  const [availableChartCountries, setAvailableChartCountries] = useState<
    ICountry[]
  >([]);
  const [selectedChart, setSelectedChart] = useState<IChartDataWithDetails>();
  const [selectedCountry, setSelectedCountry] = useState<ICountry>();
  const [offsetEntitiesKey, setOffsetEntitiesKey] = useState<any>();
  const [chartFrequency, setSelectedFrequency] = useState<string>('');
  const [availableFilteredCharts, setAvailableFilteredCharts] = useState();
  const [chartDate, setChartDate] = useState<any | null>(null);
  const [availableDates, setAvailableDates] = useState<Date[] | null>(null);
  const prevChartData = usePrevious(chartDate);
  // const [wmgTracksPercentage, setWmgTracksPercentage] = useState<number | null>(
  //   null
  // ); // TODO uncomment this part when we have correct isWMG flag from BE
  useEffect(() => {
    setChartInfo({
      entityTitle: selectedCustomer?.label,
      chartTitle: selectedChart?.label,
      countryName: selectedCountry?.label,
    });
  }, [setChartInfo, selectedCustomer, selectedChart, selectedCountry]);

  const countries = useSelector(
    (state: { countries: { data: ICountryFlagProps[] } }) =>
      state.countries.data
  );
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadCustomers());
    dispatch(loadCountries());
  }, [dispatch]);

  useEffect(() => {
    if (customers.length > 0 && countries.length > 0) {
      chartsFetch(customers, chartPage, countries);
    }
  }, [chartsFetch, customers, chartPage, countries]);

  /**
   * Use limit and offset for the API data
   */
  const { offsetState, setEntities, setInvalidate, resetOffset, setState } =
    useOffset({
      limit: 10,
      increment: 10,
      entitiesKey: offsetEntitiesKey,
    });
  const { limit, increment, offset, value: chartTracksData } = offsetState;

  const exportUrl = useExportUrl(chartTableData?.value?.url);

  useEffect(() => {
    if (chartTableData?.fulfilled && chartTableData.value.trackData) {
      setEntities({
        chartName: chartTableData.value.chartName,
        [offsetEntitiesKey]: chartTableData.value.trackData?.tracks,
        total: chartTableData.value.totalTracksCount,
      });
      setChartDate(chartTableData.value.chartDate);
      setAvailableDates(chartTableData.value.availableDates);
      // setWmgTracksPercentage(chartTableData.value.wmgTracksPercentage); // TODO uncomment this part when we have correct isWMG flag from BE
    }
  }, [chartTableData, setEntities, offsetEntitiesKey]);

  useEffect(() => {
    if (uniqueCustomers?.length) {
      setSelectedCustomer(uniqueCustomers[0]);
    }
  }, [uniqueCustomers]);

  const setChartCountries = useCallback(
    (chart: IChartDataWithDetails, countries: ICountryFlagProps[]): void => {
      if (selectedCustomer?.value === 'appleMusic') {
        const sortedCountries = countries
          .sort((a, b) => (a.displayName > b.displayName ? 1 : -1))
          .map(addLabel);
        const defaultSelectedCountry = sortedCountries.find(
          (country: any) => country.id === 'US'
        );
        setAvailableChartCountries(sortedCountries);
        setSelectedCountry(defaultSelectedCountry || sortedCountries[0]);
      } else {
        const chartCountries = orderBy(
          getAvailableChartCountries(chart, countries).map(addLabel),
          ['label'],
          ['asc']
        );
        let globalCountryType = chartCountries.find(
          (country: ICountry) => country.id === defaultCountry
        );
        if (!globalCountryType) {
          globalCountryType = chartCountries.find(
            (country: ICountry) => country.id === 'WW'
          );
        }
        setSelectedCountry(
          globalCountryType ? globalCountryType : chartCountries[0]
        );
        setAvailableChartCountries(chartCountries);
      }
    },
    [setSelectedCountry, defaultCountry, selectedCustomer]
  );

  const setChart = (
    chartsList: IChartDataWithDetails[],
    page: string,
    selectedDSP: IToggleOption | null
  ): IChartDataWithDetails => {
    const chart =
      (page === 'dsp' &&
        selectedDSP &&
        ((selectedDSP.value === 'spotify' &&
          chartsList.find(
            (chart: IChartDataWithDetails) => chart.id === 1006
          )) ||
          (selectedDSP.value === 'appleMusic' &&
            chartsList.find(
              (chart: IChartDataWithDetails) => chart.id === 1021
            )))) ||
      chartsList[0];
    setSelectedChart(chart);
    return chart;
  };
  useEffect(() => {
    // `setInvalidate` flushes the entities when the next batch of entities arrive.
    // Happens at the next `setEntities` so we don't see a skeleton loader in between.
    setInvalidate();
  }, [setInvalidate, selectedChart, selectedCountry, chartDate]);

  useEffect(() => {
    resetOffset();
    if (selectedChart && selectedCountry)
      setOffsetEntitiesKey(`${selectedChart.id}_${selectedCountry.id}_tracks`);
  }, [selectedChart, selectedCountry, resetOffset, setOffsetEntitiesKey]);

  useEffect(() => {
    if (customersAndCharts && selectedCustomer) {
      const selectedCustomerCharts: any = customersAndCharts.find(
        (customer: ICustomerWithCharts) =>
          customer.name === selectedCustomer.value
      );
      const charts = (selectedCustomerCharts.charts || [])
        .map(addLabel)
        .sort((a: any, b: any) => (a.label > b.label ? 1 : -1));
      setDSPAvailableCharts(charts);
      const chart = setChart(charts, chartPage, selectedCustomer);
      setChartCountries(chart, countries);
      setSelectedFrequency(chart.updateFrequency);
      setAvailableFilteredCharts(
        filterCharts(charts, 'updateFrequency', chart.updateFrequency)
      );
    }
  }, [
    selectedCustomer,
    customersAndCharts,
    dispatch,
    countries,
    setChartCountries,
    chartPage,
  ]);

  useEffect(() => {
    if (selectedChart && selectedChart?.customerName !== 'Apple Music') {
      setChartCountries(selectedChart, countries);
    }
  }, [selectedChart, countries, setChartCountries]);

  useEffect(() => {
    if (selectedCustomer?.value === 'appleMusic' && customersAndCharts) {
      const selectedCustomerCharts: any = customersAndCharts.find(
        (customer: ICustomerWithCharts) =>
          customer.name === selectedCustomer.value
      );
      const charts = (selectedCustomerCharts.charts || [])
        .map(addLabel)
        .filter((chart: any): any =>
          chart.supportedCountryIds.includes(selectedCountry?.id)
        );
      setDSPAvailableCharts(charts);
      setAvailableFilteredCharts(charts);
      const chart =
        charts.find((chart: IChartDataWithDetails) => chart.id === 1021) ||
        charts[0];
      setSelectedChart(chart);
    }
    // eslint-disable-next-line
  }, [selectedCountry]);

  const resetDefaultChartCountries = (
    charts: IChartDataWithDetails[]
  ): void => {
    const chart = setChart(charts, chartPage, selectedCustomer);
    setSelectedFrequency(chart.updateFrequency);
    setChartCountries(chart, countries);
  };

  useEffect(() => {
    const currentData = chartTableData?.value;
    const shouldReloadData =
      chartDate !== prevChartData ? currentData?.chartDate !== chartDate : true;
    if (selectedChart && selectedCountry && shouldReloadData) {
      chartTableFetch(
        selectedChart.id,
        selectedCountry.id,
        chartPage,
        limit,
        offset,
        chartFrequency,
        chartDate
      );
    }
  }, [
    selectedChart,
    selectedCountry,
    chartPage,
    chartTableFetch,
    limit,
    offset,
    chartFrequency,
    chartDate,
  ]);

  useEffect(() => {
    if (selectedCustomer?.value !== 'appleMusic') {
      const frequentCharts = filterCharts(
        availableDSPCharts,
        'updateFrequency',
        chartFrequency
      );
      setAvailableFilteredCharts(frequentCharts);
      const chart = frequentCharts[0];
      if (chart) {
        setSelectedChart(chart);
        setChartCountries(chart, countries);
      }
    }
  }, [
    chartFrequency,
    availableDSPCharts,
    countries,
    setChartCountries,
    selectedCustomer,
  ]);

  const showTable =
    (chartTracksData && chartTracksData[offsetEntitiesKey]) ||
    (chartTracksData &&
      isLoadedChartTableData &&
      !chartTableData?.value.trackData?.tracks.length);

  const getChartDate = useCallback(
    (chartDate: Date): string => {
      return chartDate
        ? chartFrequency === 'daily'
          ? formatDate(new Date(chartDate), 'day-short')
          : `${formatDate(new Date(chartDate), 'day-short')} - ${formatDate(
              addDays(new Date(chartDate), 6),
              'day-short'
            )}`
        : 'Date';
    },
    [chartFrequency]
  );

  useEffect(() => {
    setChartDate(null);
  }, [chartFrequency, setChartDate, selectedChart, selectedCountry]);

  const availableChartDates = useMemo(() => {
    if (availableDates) {
      return availableDates.map((date: Date) => {
        return {
          label: getChartDate(date),
          value: date,
        };
      });
    }
    return [];
  }, [availableDates, getChartDate]);

  const setCustomer = (customer: any): void => {
    const appleCustomer = {
      value: 'apple',
      label: 'Apple',
      positionsCount: customer?.positionsCount,
    };
    const selectedCustomer =
      customer?.value === 'itunes' ? appleCustomer : customer;
    setSelectedCustomer(selectedCustomer);
  };

  const showAllItems = (): void => {
    setState({ limit: chartTracksData.total - limit, offset: increment });
  };

  return (
    <ErrorBoundary error={reason}>
      <ChartsTabs
        chartPage={chartPage}
        isLargeScreen={isLargeScreen}
        title={title}
        isLoadingCustomers={isLoadingCustomers}
        selectedCustomer={selectedCustomer}
        uniqueCustomers={uniqueCustomers}
        setSelectedCustomer={setCustomer}
        variation={variation}
      />

      <ChartsTopicFilters
        chartsList={availableDSPCharts}
        filteredChartsList={availableFilteredCharts}
        countriesList={availableChartCountries}
        selectedChart={selectedChart}
        selectedChartDate={
          chartDate
            ? {
                label: getChartDate(chartDate),
                value: chartDate,
              }
            : undefined
        }
        selectedCountry={selectedCountry}
        setSelectedChart={setSelectedChart}
        setSelectedCountry={setSelectedCountry}
        setSelectedFrequency={setSelectedFrequency}
        resetDefaultChartCountries={resetDefaultChartCountries}
        availableDates={availableChartDates}
        setSelectedDate={setChartDate}
        isLoadedCustomers={isLoadedCustomers}
      />

      <TableLayout>
        <AnimatedLoadingWrapper loading={isLoadingChartTableData}>
          {showTable ? (
            selectedChart && selectedCountry ? (
              <>
                <ChartTable
                  dataCy="chart-table-section"
                  // wmgRepresents={
                  //   wmgTracksPercentage
                  //     ? formatNumber(wmgTracksPercentage, 'percentage', true)
                  //     : null
                  // } // TODO uncomment this part when we have correct isWMG flag from BE
                  wmgRepresents={null}
                  chartPage={chartPage}
                  chartName={selectedChart?.displayName}
                  chartFrequency={chartFrequency}
                  tracks={chartTracksData[offsetEntitiesKey]}
                  selectedCountryName={selectedCountry?.displayName}
                  exportUrl={exportUrl}
                />
                {chartTracksData[offsetEntitiesKey] && (
                  <PaginationWrapper>
                    <Pagination
                      current={chartTracksData[offsetEntitiesKey].length}
                      total={chartTracksData.total || null}
                      offset={chartTracksData.total}
                      hasMoreResults={
                        chartTracksData[offsetEntitiesKey].length <
                        chartTracksData.total
                      }
                      onClick={showAllItems}
                      isWideButtonWithTotal
                    />
                  </PaginationWrapper>
                )}
              </>
            ) : (
              <Layout.Relative>
                <Box p={5}>
                  <Placeholder
                    type={'data'}
                    entityType={'chart'}
                    isSmallScreen={!isLargeScreen}
                  />
                </Box>
              </Layout.Relative>
            )
          ) : (
            <ChartTableLoader isLargeScreen={isLargeScreen} />
          )}
        </AnimatedLoadingWrapper>
      </TableLayout>
    </ErrorBoundary>
  );
};

export default interceptConnect<any>(dataSources)(Charts);
